import React, { useEffect, useState } from "react";
import { Button, Modal, Form, Input, Switch, Table } from "antd";
import PageHeader from "../../components/PageHeader";
import Footer from "../../components/Footer";
import Breadcrumb from "../../components/Breadcrumb";
import {
  PageWrapper,
  DashboardMain,
  DashboardSection,
  ListHeader,
  ModuleCardTitle,
} from "./styles";
import { useContent, NewsItem } from "../../hooks/content";
import useAdminGuard from "../../hooks/useAdminGuard";

const News: React.FC = () => {
  useAdminGuard();
  const { listNews, saveNews, removeNews } = useContent();
  const [items, setItems] = useState<NewsItem[]>([]);
  const [visible, setVisible] = useState(false);
  const [editing, setEditing] = useState<NewsItem | null>(null);
  const [form] = Form.useForm();

  const load = React.useCallback(async () => {
    try {
      const data = await listNews();
      setItems(data);
    } catch (error) {
      console.error(error);
    }
  }, [listNews]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setVisible(true);
  };

  const openEdit = (item: NewsItem) => {
    setEditing(item);
    form.setFieldsValue({
      title: item.title,
      description: item.description,
      published: item.published,
    });
    setVisible(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    await saveNews({
      uid: editing?.uid,
      title: values.title,
      description: values.description,
      published: values.published ?? false,
    });
    setVisible(false);
    await load();
  };

  const handleRemove = async (uid: string) => {
    await removeNews(uid);
    await load();
  };

  const columns = [
    {
      title: "Título",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Descrição",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Publicado",
      dataIndex: "published",
      key: "published",
      render: (value: boolean) => (value ? "Sim" : "Não"),
    },
    {
      title: "Ações",
      key: "actions",
      render: (_: any, record: NewsItem) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button size="small" type="primary" onClick={() => openEdit(record)}>
            Editar
          </Button>
          <Button size="small" danger onClick={() => handleRemove(record.uid)}>
            Excluir
          </Button>
        </div>
      ),
    },
  ];

  return (
    <PageWrapper id="page">
      <PageHeader
        title="Gerenciamento de Notícias"
        description="Gerencie as notícias e atualizações do aplicativo"
        type="private"
      />
      <DashboardMain className="dashboard-main">
        <Breadcrumb
          items={[
            { label: "Gerenciamento", link: "/manager" },
            { label: "Notícias", active: true },
          ]}
        />
        <DashboardSection className="dashboard-section">
          <ListHeader>
            <ModuleCardTitle className="module-card__title">
              Lista de Notícias
            </ModuleCardTitle>
            <Button type="primary" onClick={openCreate}>
              + Nova notícia
            </Button>
          </ListHeader>
          <Table
            rowKey="uid"
            columns={columns}
            dataSource={items}
            pagination={{ hideOnSinglePage: true, pageSize: 10 }}
          />
        </DashboardSection>
      </DashboardMain>
      <Footer />

      <Modal
        open={visible}
        title={editing ? "Editar notícia" : "Nova notícia"}
        onCancel={() => setVisible(false)}
        onOk={handleSubmit}
        okText="Salvar"
        cancelText="Cancelar"
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            name="title"
            label="Título"
            rules={[{ required: true, message: "Informe o título" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="Descrição"
            rules={[{ required: true, message: "Informe a descrição" }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item name="published" label="Publicado" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </PageWrapper>
  );
};

export default News;
